import { useEffect, useState } from "react";
import { Copy, X, WhatsappLogo, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Global fallback modal — listens for "whatsapp:fallback" events from waOpener.js
 * and shows the user the customer number + message + copy button so they can paste
 * into WhatsApp manually if the popup was blocked or the deep-link failed.
 *
 * Mount once at the App-level (e.g. inside <App /> just before the routes).
 */
const WhatsappFallbackModal = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const onFallback = (e) => setData(e.detail || {});
    window.addEventListener("whatsapp:fallback", onFallback);
    return () => window.removeEventListener("whatsapp:fallback", onFallback);
  }, []);

  if (!data) return null;
  const { phone = "", message = "", url = "" } = data;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message || "");
      toast.success("Message copied — paste into WhatsApp");
    } catch {
      toast.error("Couldn't copy. Long-press to select & copy.");
    }
  };

  const reopen = () => {
    if (!url) { setData(null); return; }
    const w = window.open(url, "_blank");
    if (!w || w.closed) toast.error("Browser blocked the popup. Allow popups for this site.");
    setData(null);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="wa-fallback-modal">
      <div className="bg-white border border-slate-200 w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" className="text-[#25D366]" />
            <h3 className="text-lg font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>WhatsApp couldn't open automatically</h3>
          </div>
          <button data-testid="wa-fallback-close" onClick={() => setData(null)} className="p-1 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Customer WhatsApp Number</div>
            <div data-testid="wa-fallback-phone" className="font-mono text-slate-900 bg-slate-50 border border-slate-200 p-2.5">
              {phone ? `+${phone}` : <span className="text-rose-700">No number on this contact</span>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Message Preview</div>
            <textarea
              data-testid="wa-fallback-message"
              readOnly
              value={message}
              className="w-full bg-slate-50 border border-slate-200 p-2.5 text-sm font-mono"
              rows="6"
            />
          </div>
          <div className="flex gap-2">
            <button
              data-testid="wa-fallback-copy"
              onClick={copy}
              className="flex-1 rounded-none border border-slate-300 hover:bg-slate-50 text-slate-800 px-4 py-2.5 font-medium inline-flex items-center justify-center gap-2"
            >
              <Copy size={14} /> Copy Message
            </button>
            <button
              data-testid="wa-fallback-reopen"
              onClick={reopen}
              disabled={!url}
              className="flex-1 rounded-none bg-[#25D366] text-white hover:bg-[#1ba851] disabled:opacity-50 px-4 py-2.5 font-medium inline-flex items-center justify-center gap-2"
            >
              <ArrowSquareOut size={14} weight="bold" /> Open WhatsApp
            </button>
          </div>
          <div className="text-[10px] text-slate-500 text-center">
            Tip: allow popups for this site so future clicks open WhatsApp directly.
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsappFallbackModal;
